'use strict';


import {
    Box3, Box3Helper, BoxGeometry,
    DynamicDrawUsage,
    Euler,
    Group,
    InstancedMesh,
    LinearSRGBColorSpace,
    Matrix4, Mesh, MeshBasicMaterial,
    Quaternion,
    Vector3,
} from 'three';
import { SelectedObjectType, ViewMode } from '../../common';
import { Buildings, projectMode, settingsData, threeJsVariables } from '../../settings';
import { createBuildingInfo } from '../threejs/autoSelectionBoxes';
import { ColorCorrection } from '../threejs/colorCorrection';
import { hideSceneObjects, sceneObjectsGroup, showSceneObjects } from '../points/points';
import { Objects } from '../threejs/Objects/Objects';

const _matrix = new Matrix4();
const _position = new Vector3();
const _quaternion = new Quaternion();
const _scale = new Vector3();
const _box = new Box3();

export class Building {
    constructor (model) {
        this.model = model;
        this.modelGroup = new Group();
        this.selectionsGroup = null;
        this.name = this.model.name;
        this.apartments = [];
        this.floors = [];
        this.amenities = {};
        this.instances = [];
        this.selections = [];
        this.floorsPlug = [];
        this.customSelections = [];
        this.cylinderLabels = [];
        this.info = {};
        this.isActive = false;
        this.isHide = false;
        this.mouseHover = false;
        this.floorSliderNumbers = [];
        this.colors = new ColorCorrection().getColors('buildings');
        this.params = {
            bboxObjects: []
        }
    }

    create() {
        /* Create main Group */
        this.modelGroup.name = this.model.name;

        if (!threeJsVariables.slicer.active) {
            /* Sort floors by Y */
            this.model.children = this.model.children.sort((prev, next) => prev.position.y - next.position.y);

            this.modelGroup.position.set(this.model.position.x, this.model.position.y, this.model.position.z);
            this.modelGroup.scale.set(this.model.scale.x, this.model.scale.y, this.model.scale.z);
            this.modelGroup.rotation.set(this.model.rotation.x, this.model.rotation.y, this.model.rotation.z);

            let instanceMesh;

            /* Create floor instance Mesh */
            this.model.children.forEach(floor => {
                if (floor.userData.groups) {
                    floor.geometry.groups.push(...floor.userData.groups);
                }
                if (floor.name.match(/floor/g)) {
                    instanceMesh = makeInstanceMesh(this,floor, undefined, 'floor');
                    instanceMesh.geometry.computeBoundsTree();
                    this.instances.push(instanceMesh);
                    this.modelGroup.add(instanceMesh);
                } else {
                    instanceMesh = makeInstanceMesh(this,floor, 1, 'amenity');
                    instanceMesh.geometry.computeBoundsTree();
                    this.instances.push(instanceMesh);
                    this.modelGroup.add(instanceMesh);
                }
            });
        } else {
            this.modelGroup.position.set(this.model.parent.position.x, this.model.parent.position.y, this.model.parent.position.z);
            this.modelGroup.scale.set(this.model.parent.scale.x, this.model.parent.scale.y, this.model.parent.scale.z);
            this.modelGroup.rotation.set(this.model.parent.rotation.x, this.model.parent.rotation.y, this.model.parent.rotation.z);


            if (this.model.userData.groups) {
                this.model.geometry.groups.push(...this.model.userData.groups);
            }
            this.model.userData.type = SelectedObjectType.Building;
            this.model.geometry.computeBoundsTree();
            // this.model.layers.set(1);
            // this.model.userData.main_building_item = true;
            // this.model.userData.buildingFloors = true;
            this.instances.push(this.model);
            this.modelGroup.add(this.model);
            this.amenities.Roof = true;
        }


        if (projectMode.LiteMode.isActive() && (!projectMode.LiteMode.modeIs('regular') && !projectMode.LiteMode.modeIs('schematic') && !projectMode.LiteMode.modeIs('rotate') ))
            this.modelGroup = null;

        if (!projectMode.LiteMode.isActive() || projectMode.LiteMode.isActive() && (projectMode.LiteMode.modeIs('regular') || projectMode.LiteMode.modeIs('rotate'))) {
            this.floorSliderNumbers = this.floors.map(el => el.floor).sort((a,b) => b - a);
        }


        /* Add model to intersection */
        if (!projectMode.LiteMode.isActive() && !projectMode.AirMode.active && !projectMode.AirMode.isSchematicBuildings())
            settingsData.objects_to_intersection.push(...this.instances);
        
        this.cylinderLabels = Objects.cylinderCSSLabels.filter(label => label.data.buildingEngineId === this.name);
        settingsData.instanced_floors_switch[this.name] = this.instances;
        /* Add building to District */
        if (Buildings.isUsedBuilding(this.name)) {
            Buildings.addBuilding(this.name,this);
        }

        return this.modelGroup;
    }

    addFloor(value){
        this.floors.push(value);
    }

    applyColorCorrection() {
        if (threeJsVariables.bundleConfig?.version) return;

        this.instances.forEach(instance => {
           if (Array.isArray(instance.material)) {
               instance.material.forEach(singleMaterial => {
                   singleMaterial.color.setStyle(this.colors.color,LinearSRGBColorSpace);
                   singleMaterial.emissive.setStyle(this.colors.emissive,LinearSRGBColorSpace);
               });
           } else {
               instance.material.color.setStyle(this.colors.color,LinearSRGBColorSpace);
               instance.material.emissive.setStyle(this.colors.emissive,LinearSRGBColorSpace);
           }
        });
    }

    addAmenities(key,value) {
        if (projectMode.LiteMode.isActive() && !projectMode.LiteMode.modeIs('regular') && !projectMode.LiteMode.modeIs('rotate')) return;
        this.amenities[key] = value;
    }

    addSelection(data){
        this.selections.push(data);
    }

    addInfo(value){
        this.info = value;
    }

    getInfo(){
        if (Object.keys(this.info).length){
            return this.info;
        } else {
            return false;
        }
    }

    getFloorPlugMeshes(){
        return this.floorsPlug.map(plug => plug.data);
    }

    getFloorsMoreThan(number) {
        if (projectMode.LiteMode.isActive() && !projectMode.LiteMode.modeIs('regular') && !projectMode.LiteMode.modeIs('rotate'))
            return [];
        return this.floors.filter(obj => obj.floor > number).map(el => el.data);
    }

    getFloorSelections(number, withPlug = true){
        if (withPlug){
            return this.selections.find(selection => selection.floor === number).data;
        } else {
            return this.selections.find(selection => selection.floor === number).data
                .filter(apt => !apt.name.startsWith(config.resources.bundle.selectionBoxes.floorPlugName));
        }
    }

    getFloorsSelectionsGroupMoreThan(number) {
        return this.selections.filter(selection => selection.floor > number).reduce((acc,value) => {
            acc.push(value.data[0].parent);
            return acc;
        },[]);
    }

    getFloors(start,end) {
        let floors = [];
        let floorSelectionGroup = [];

        if (end === undefined) {
            floors = this.getFloorsMoreThan(start);
            floorSelectionGroup = this.getFloorsSelectionsGroupMoreThan(start);
        } else {
            floors = this.floors.filter(obj => obj.floor > start && obj.floor <= end).map(el => el.data);
            floorSelectionGroup = this.selections.filter(selection => selection.floor > start && selection.floor <= end).reduce((acc,value) => {
                acc.push(value.data[0].parent);
                return acc;
            },[]);
        }

        return {
            floors,
            floorSelectionGroup
        }
    }

    getFloorNumbersForFloorSlider() {
        const floorsWithoutCRMData = [];
        this.floorSliderNumbers.forEach(floor => {
            let isFloorEmpty = true;
            this.getFloorSelections(floor, false).forEach(selection => {
                if (selection.userData?.crm_data)
                    isFloorEmpty = false;
            });
            if (isFloorEmpty)
                floorsWithoutCRMData.push(floor);
        });

        return this.floorSliderNumbers.filter(item => !floorsWithoutCRMData.includes(item));
    }

    activate(){
        this.setLayer(1);
        this.isActive = true;
    }

    deactivate(){
        this.setLayer(0);
        this.isActive = false;
    }

    setLayer(value) {
        this.modelGroup.traverse(el => {
            if (el.isMesh) {
                el.layers.set(value);
            }
        });
    }

    hideSceneObjectsOfCurrentBuilding() {
        if (!threeJsVariables.slicer.active) return;

        const objects = this.params.bboxObjects;

        if (objects.length) {
            const names = [...new Set(objects.map(el => el.name))];
            names.forEach(objectName => {
                const points = objects.filter(el => el.name === objectName);
                const onScene = threeJsVariables.scene.getObjectByName(objectName);

                points.forEach(point => {
                    onScene.getMatrixAt(point.count,_matrix);
                    _matrix.decompose(_position,_quaternion,_scale);
                    _matrix.setPosition(_position.x,-1000,_position.z);
                    onScene.setMatrixAt(point.count,_matrix);
                });
                onScene.instanceMatrix && (onScene.instanceMatrix.needsUpdate = true);
                onScene.instanceMatrix && onScene.computeBoundingSphere();
            });

        } else {
            const models = config.resources.sceneObjects.models || [];
            const manualAttached = models.filter(el => el.attach === this.name).map(el => el.name);
            _box.makeEmpty();
            _box.setFromObject(this.modelGroup);
            _box.expandByScalar(1.05);

            sceneObjectsGroup.traverse(el => {
                if (el.isInstancedMesh && !el.isInstanceLOD) {
                    for (let i = 0; i < el.count; i++) {
                        el.getMatrixAt(i,_matrix);
                        _matrix.decompose(_position,_quaternion,_scale);
                        if (_box.containsPoint(_position)) {
                            this.params.bboxObjects.push({
                                name: el.name,
                                count: i,
                                matrix: new Matrix4().copy(_matrix)
                            });
                            _matrix.setPosition(_position.x,-1000,_position.z);
                            el.setMatrixAt(i,_matrix);
                        }
                    }

                    el.instanceMatrix.needsUpdate = true;
                    el.computeBoundingSphere();
                } else if (el.isInstanceLODObject) {
                    for (let i = 0; i < el.count; i++) {
                        _matrix.copy(el.getMatrixAt(i));
                        _matrix.decompose(_position,_quaternion,_scale);
                        if (_box.containsPoint(_position)) {
                            this.params.bboxObjects.push({
                                name: el.name,
                                count: i,
                                matrix: new Matrix4().copy(_matrix)
                            });
                            _matrix.setPosition(_position.x,-1000,_position.z);
                            el.setMatrixAt(i,_matrix);
                        }
                    }
                } else if (el.isMesh && !el.isInstancedMesh && !el.isInstanceLOD) {
                    if (!manualAttached.includes(el.name)) {
                        el.getWorldPosition(_position);
                        if (_box.containsPoint(_position)) {
                            this.params.bboxObjects.push({
                                name: el.name,
                                position: el.position.clone()
                            });
                            el.position.setY(-1000);
                        }
                    }
                }
            });

            if (objects.length && threeJsVariables.sceneObjectsSlicer.active) {
                const names = [...new Set(objects.map(el => el.name))];
                if (!Array.isArray(config.resources.sceneObjects.models)) {
                    config.resources.sceneObjects.models = [];
                }

                names.forEach(name => {
                    const onScene = threeJsVariables.scene.getObjectByName(name);
                    if (onScene.isInstanceLODObject) {
                        const subNames = onScene.children.map(subMesh => subMesh.name);
                        subNames.forEach(subName => {
                            config.resources.sceneObjects.models.push({
                                name: subName,
                                attach: this.name
                            });
                        });
                    } else {
                        config.resources.sceneObjects.models.push({
                            name,
                            attach: this.name
                        });
                    }
                });
            }
        }
    }

    updateAttachedModelsList() {
        if (!threeJsVariables.sceneObjectsSlicer.active) return;

        const models = config.resources.sceneObjects.models || [];
        const manualAttached = models.filter(el => el.attach === this.name).map(el => el.name);
        _box.makeEmpty();
        _box.setFromObject(this.modelGroup);
        _box.expandByScalar(1.05);
        const names = [];

        sceneObjectsGroup.traverse(el => {
            if (el.isInstancedMesh && !el.isInstanceLOD) {
                for (let i = 0; i < el.count; i++) {
                    el.getMatrixAt(i,_matrix);
                    _matrix.decompose(_position,_quaternion,_scale);
                    if (_box.containsPoint(_position)) {
                       names.push(el.name);
                    }
                }
            } else if (el.isInstanceLODObject) {
                for (let i = 0; i < el.count; i++) {
                    _matrix.copy(el.getMatrixAt(i));
                    _matrix.decompose(_position,_quaternion,_scale);
                    if (_box.containsPoint(_position)) {
                        names.push(el.name);
                    }
                }
            } else if (el.isMesh && !el.isInstancedMesh && !el.isInstanceLOD) {
                if (!manualAttached.includes(el.name)) {
                    el.getWorldPosition(_position);
                    if (_box.containsPoint(_position)) {
                        names.push(el.name);
                    }
                }
            }
        });

        if (names.length) {
            const uniqueNames = [...new Set(names)];

            uniqueNames.forEach(name => {
                const onScene = threeJsVariables.scene.getObjectByName(name);
                if (onScene.isInstanceLODObject) {
                    const subNames = onScene.children.map(subMesh => subMesh.name);
                    subNames.forEach(subName => {
                        config.resources.sceneObjects.models.push({
                            name: subName,
                            attach: this.name
                        });
                    });
                } else {
                    config.resources.sceneObjects.models.push({
                        name,
                        attach: this.name
                    });
                }
            });

            if (settingsData.activeViewMode !== ViewMode.Orbit && this.isActive) {
                hideSceneObjects(this);
                showSceneObjects(this);
            }
        }
    }

    showSceneObjectsOfCurrentBuilding() {
        const objects = this.params.bboxObjects;

        if (objects.length) {
            const names = [...new Set(objects.map(el => el.name))];
            names.forEach(objectName => {
                const points = objects.filter(el => el.name === objectName);
                const onScene = threeJsVariables.scene.getObjectByName(objectName);

                if (onScene.isInstancedMesh && !onScene.isInstanceLOD) {
                    points.forEach(point => {
                        onScene.setMatrixAt(point.count, point.matrix);
                    });
                    onScene.instanceMatrix.needsUpdate = true;
                    onScene.computeBoundingSphere();

                } else if (onScene.isInstanceLODObject) {
                    points.forEach(point => {
                        onScene.setMatrixAt(point.count, point.matrix);
                    });
                } else if (onScene.isMesh && !onScene.isInstancedMesh && !onScene.isInstanceLOD) {
                    points.forEach(point => {
                        onScene.position.copy(point.position);
                    });
                }
            });
        }
    }

    bboxVisibility(state,customScalar) {
        _box.makeEmpty();
        _box.setFromObject(this.modelGroup);
        if (customScalar) {
            _box.expandByScalar(customScalar);
        }

        if (state) {
            if (!this.params.bboxHelper) {
                this.params.bboxHelper = new Box3Helper(_box);
            }

            if (this.params.bboxHelper.parent) {
                this.params.bboxHelper.removeFromParent();
            }

            threeJsVariables.scene.add(this.params.bboxHelper);
        } else {
            if (this.params.bboxHelper) {
                this.params.bboxHelper.removeFromParent();
            }
        }
    }
}

export class BuildingWithoutModel extends Building{

    constructor (model,props,settings) {
        super(model);
        this.name = model;
        this.info = createBuildingInfo({name: this.model},props,settings);

        return this;
    }

    getApartments() {
        return this.apartments;
    }
}


export class BuildingBBOX extends Building{
    constructor (model) {
        super(model);

    }

    create () {
       this.name = this.model.name.split('_')[0];
       this.modelGroup.name = this.name;
       const bbox = new Box3();
       const size = new Vector3();
       const position = new Vector3();
       this.model.getWorldPosition(position);
       this.modelGroup.position.copy(position);
       bbox.setFromObject(this.model);
       const distance = bbox.distanceToPoint(position);
       bbox.getSize(size);
       const material = new MeshBasicMaterial({
           // transparent: true,
           // opacity: 0
       });

       const dimensions = new Vector3().subVectors( bbox.max, bbox.min );
       const boxGeo = new BoxGeometry(dimensions.x, dimensions.y, dimensions.z);
       const matrix = new Matrix4().setPosition(dimensions.addVectors(bbox.min, bbox.max).multiplyScalar( 0.5 ));
       boxGeo.applyMatrix4(matrix);
       boxGeo.center();
       boxGeo.translate(0,size.y / 2 + distance,0);

       const mesh = new Mesh(boxGeo,material);
       mesh.visible = false;

       mesh.userData.type = SelectedObjectType.Building;
       mesh.geometry.computeBoundsTree();
       this.instances.push(mesh);
       this.modelGroup.add(mesh);
       this.amenities.Roof = true;

       settingsData.objects_to_intersection.push(...this.instances);
       settingsData.instanced_floors_switch[this.name] = this.instances;
       const projectsGroup = threeJsVariables.scene.getObjectByName('Project Buildings');
       if (projectsGroup) {
           projectsGroup.add(this.modelGroup);
       }

       if (Buildings.isUsedBuilding(this.name)) {
           Buildings.addBuilding(this.name,this);
       }
    
       this.cylinderLabels = Objects.cylinderCSSLabels.filter(label => label.data.buildingEngineId === this.name);

       return this;
    }

    getApartments() {
        return this.apartments;
    }
}

function makeInstanceMesh(scope,baseMesh, count, type) {
    let rotation = new Euler(baseMesh.rotation.x, baseMesh.rotation.y, baseMesh.rotation.z, 'XYZ');
    let rotationQ = new Quaternion().setFromEuler(rotation);
    let matrix = new Matrix4();
    let scale = new Vector3(baseMesh.scale.x, baseMesh.scale.y, baseMesh.scale.z);
    let position;
    let floorHeight = 0;
    let from, to = 0;
    let iteration = (count) ? count : 0;
    let isFloor = false;
    let key;
    let value;
    let floorFrom;
    let floorTo;

    if (type === 'floor') {
        let [buildingName, floorName, modelFloorHeight] = baseMesh.name.split('_');
        const floorHeightInMeter = parseFloat(modelFloorHeight) / 100;
        floorName = floorName.replace('floor','');

        /* Checking floor TO value */
        if (floorName.match(/((-\d{1,})|(\d{1,})).((-\d{1,})|(\d{1,}))/g)){
            const split = floorName.split('-');
            floorFrom = split[0];
            floorTo = split[1];
        } else {
            floorFrom = floorName;
            floorTo = floorName;
        }

        from = Number(floorFrom);
        to = Number(floorTo);
        floorHeight = floorHeightInMeter;
        iteration =  to - from;

        if (iteration === 0){
            iteration = 1;
        } else {
            iteration++;
        }
        isFloor = true;
    }

    let elementBuildingInstance = new InstancedMesh(baseMesh.geometry, baseMesh.material, iteration);
    elementBuildingInstance.instanceMatrix.setUsage(DynamicDrawUsage);

    for (let i = 0; i < iteration; i++) {
        const floorPosY = baseMesh.position.y + i * floorHeight;
        position = new Vector3(baseMesh.position.x, floorPosY, baseMesh.position.z);
        matrix.compose(position, rotationQ, scale);
        elementBuildingInstance.setMatrixAt(i, matrix);
        elementBuildingInstance.renderOrder = 1;

        switch (type) {
            case 'floor':
                key = from + i;
                value = {
                    'name': baseMesh.name,
                    'mesh': elementBuildingInstance,
                    'count': i,
                    'position': {x: position.x, y: position.y, z: position.z},
                    'basePosition': {x: position.x, y: position.y, z: position.z},
                    'rotation': rotation,
                    'diff': {
                        x: 0,
                        y: position.y,
                        z: 0,
                    },
                    'matrix': matrix
                };
                const floorObj = {floor: key, data: value};
                scope.addFloor(floorObj);
                break;
            case 'amenity':
                key = baseMesh.name;
                key = key.replace(/.\d{1,}/g,'');
                value = {
                    'mesh': elementBuildingInstance
                };
                scope.addAmenities(key,value);
                break;
        }
    }
    elementBuildingInstance.name = `${baseMesh.name}_instance`;
    elementBuildingInstance.userData = {
        type: SelectedObjectType.Building,
        main_building_item: true,
        buildingFloors: true,
    };
    elementBuildingInstance.frustumCulled = false;
    elementBuildingInstance.receiveShadow = true;
    elementBuildingInstance.castShadow = true;
    // elementBuildingInstance.layers.set(1);

    return elementBuildingInstance;
}
